import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  CircularProgress,
  Container,
  Alert,
  AlertTitle,
  Chip
} from "@mui/material";

// アイコン
import TuneIcon from '@mui/icons-material/Tune';
import SortIcon from '@mui/icons-material/Sort';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

// コンポーネント
import FilterModal from "../../components/FilterModal";
import SortDropdown from "../../components/SortDropdown";
import Header from "../../components/Header";
import GroupSection from "./ClientList/GroupSection";
import ClientSection from "./ClientList/ClientSection";
import { useApi } from "./ClientList/useApi";

// 🚀 新しい状態管理フックに変更
import { useSessionClientState } from "./ClientList/useSessionClientState";

import { useNavigate } from "react-router-dom";
import "./ClientList.css";

export default function ClientList() {
  /* ------------------------------------------------------------------ */
  /* ページネーション＆モーダル                                         */
  /* ------------------------------------------------------------------ */
  const [pageSize] = useState(20);
  const [isFilterOpen, setFilterOpen] = useState(false);
  const [isSortOpen, setSortOpen] = useState(false);
  const navigate = useNavigate();

  /* ------------------------------------------------------------------ */
  /* 🚀 新しい状態管理フック（sessionStorage一本化）                    */
  /* ------------------------------------------------------------------ */
  const {
    searchTerm,
    searchQuery,
    selectedGroupIds,
    activeSorting,
    allFilters,
    page,
    activeFilters,
    selectedProductId,
    setSearchTerm,
    setSelectedGroupIds,
    setActiveSorting,
    setAllFilters,
    setActiveFilters,
    setSelectedProductId,
    handleProductSelect,
    handleFilterApply,
    handleSortApply,
    handlePageChange,
    restoreState,
    persistState,
    clearState
  } = useSessionClientState();

  /* ------------------------------------------------------------------ */
  /* API ロジック（変更なし）                                             */
  /* ------------------------------------------------------------------ */
  const {
    clients,
    groups,
    products,
    loading,
    error,
    totalPages,
    totalElements,
    fetchClients,
    fetchInitialData,
    isInitialized
  } = useApi(navigate, pageSize, page, (newPage) => {
    handlePageChange(newPage);
  });

  // 製品データの安全性チェック（変更なし）
  const safeProducts = useMemo(() => {
    if (!Array.isArray(products)) return [];
    
    return products.filter(product => 
      product && 
      product.id && 
      product.name && 
      product.active !== false
    );
  }, [products]);

  const isAllSelected = selectedProductId === 'all';

  /* ------------------------------------------------------------------ */
  /* 🚀 初期化処理の修正（製品選択保持対応）                              */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    const initializeData = async () => {
      try {
        // 1. 状態復元（最優先）
        const { productId: restoredProduct, hasStoredState } = restoreState();

        // 2. 初期データ取得
        const determinedProductId = await fetchInitialData(restoredProduct);

        // 3. 製品IDの最終決定（復元された値を最優先）
        let finalProductId;
        
        if (hasStoredState && restoredProduct !== null && restoredProduct !== undefined) {
          // 🚀 重要: sessionStorageから復元された値がある場合は最優先
          finalProductId = restoredProduct;
        } else {
          // 復元された値がない場合のみ、APIの判定結果を使用
          finalProductId = determinedProductId;
        }
        
        // 有効性チェック（safeProductsが取得できてから）
        if (safeProducts.length > 0) {
          if (finalProductId !== 'all' && finalProductId && 
              !safeProducts.find(p => p.id === finalProductId)) {
            finalProductId = 'all';
          }
        }

        // 4. 製品選択を確定（状態が異なる場合のみ更新）
        if (finalProductId !== selectedProductId) {
          handleProductSelect(finalProductId);
        }

      } catch (error) {
        handleProductSelect('all');
      }
    };

    // 初回マウント時のみ実行
    initializeData();
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 空の依存配列で初回のみ実行

  /* ------------------------------------------------------------------ */
  /* 🚀 データ取得処理の安定化                                          */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!isInitialized || selectedProductId === null) return;
    
    // 状態が確定してからAPI呼び出し
    const timer = setTimeout(() => {
      const groupIds = selectedGroupIds?.length ? selectedGroupIds : [];
      fetchClients(page, searchQuery, groupIds, selectedProductId, activeSorting, allFilters);
    }, 50); // 50msの遅延で確実に状態を反映

    return () => clearTimeout(timer);
  }, [
    isInitialized, 
    page, 
    searchQuery, 
    selectedGroupIds, 
    selectedProductId, 
    activeSorting, 
    allFilters, 
    fetchClients
  ]);

  // 🚀 製品データ取得後の有効性チェック（非同期対応）
  useEffect(() => {
    // 製品データが取得された後に、保存された製品IDの有効性をチェック
    if (safeProducts.length > 0 && selectedProductId !== null && selectedProductId !== 'all') {
      const isValidProduct = safeProducts.some(p => p && p.id === selectedProductId);
      
      if (!isValidProduct) {
        handleProductSelect('all');
      }
    }
  }, [safeProducts, selectedProductId, handleProductSelect]);

  // 製品データ変更時の安全性チェック（変更なし）
  useEffect(() => {
    if (
      selectedProductId !== 'all' &&
      selectedProductId !== null &&
      safeProducts.length > 0 &&
      !safeProducts.some(p => p && p.id === selectedProductId)
    ) {
      handleProductSelect(safeProducts.length > 0 ? 'all' : null);
    }
  }, [safeProducts, selectedProductId, handleProductSelect]);

  // 製品データ変更時の安全性チェック（変更なし）
  useEffect(() => {
    if (safeProducts.length === 0 && selectedProductId !== null) {
      setSelectedProductId(null);
    }
  }, [safeProducts.length, selectedProductId, setSelectedProductId]);

  const getSortingText = (opt) => {
    switch (opt) {
      case 'remaining_asc': return '残量: 少ない順';
      case 'remaining_desc': return '残量: 多い順';
      case 'ratio_asc': return '割合: 低い順';
      case 'ratio_desc': return '割合: 高い順';
      default: return '';
    }
  };

  // アクティブフィルターチップの表示ロジック（変更なし）
  const activeFilterChips = useMemo(() => {
    const chips = [];
    
    // グループフィルター
    if (selectedGroupIds.length) {
      chips.push({ 
        label: `グループ: ${selectedGroupIds.length}件`, 
        key: 'groups', 
        onDelete: () => {
          setSelectedGroupIds([]);
          handleFilterApply({
            selectedGroups: [],
            ...allFilters
          });
        }
      });
    }
    
    // データ不完全フィルター
    if (allFilters.includeUnregistered === false) {
      chips.push({ 
        label: 'データ不完全顧客除外', 
        key: 'incomplete', 
        onDelete: () => {
          const newFilters = { ...allFilters, includeUnregistered: true };
          setAllFilters(newFilters);
          handleFilterApply({
            selectedGroups: selectedGroupIds,
            ...newFilters
          });
        }
      });
    }
    
    // 製品関連フィルター：特定製品選択時のみ表示
    if (!isAllSelected) {
      if (allFilters.remainingMin || allFilters.remainingMax) {
        const t = [
          allFilters.remainingMin ? `${allFilters.remainingMin}L以上` : '', 
          allFilters.remainingMax ? `${allFilters.remainingMax}L以下` : ''
        ].filter(Boolean).join('、');
        chips.push({ 
          label: `残量: ${t}`, 
          key: 'remaining', 
          onDelete: () => {
            const newFilters = { ...allFilters, remainingMin: '', remainingMax: '' };
            setAllFilters(newFilters);
            handleFilterApply({
              selectedGroups: selectedGroupIds,
              ...newFilters
            });
          }
        });
      }
      if (allFilters.ratioMin || allFilters.ratioMax) {
        const t = [
          allFilters.ratioMin ? `${allFilters.ratioMin}%以上` : '', 
          allFilters.ratioMax ? `${allFilters.ratioMax}%以下` : ''
        ].filter(Boolean).join('、');
        chips.push({ 
          label: `割合: ${t}`, 
          key: 'ratio', 
          onDelete: () => {
            const newFilters = { ...allFilters, ratioMin: '', ratioMax: '' };
            setAllFilters(newFilters);
            handleFilterApply({
              selectedGroups: selectedGroupIds,
              ...newFilters
            });
          }
        });
      }
      if (allFilters.hideNoData) {
        chips.push({ 
          label: '未計測顧客非表示', 
          key: 'hideNoData', 
          onDelete: () => {
            const newFilters = { ...allFilters, hideNoData: false };
            setAllFilters(newFilters);
            handleFilterApply({
              selectedGroups: selectedGroupIds,
              ...newFilters
            });
          }
        });
      }
    }
    return chips;
  }, [selectedGroupIds, allFilters, isAllSelected, setSelectedGroupIds, setAllFilters, handleFilterApply]);

  // 現在選択中の製品の安全性チェック（変更なし）
  const selectedProductForModal = useMemo(() => {
    if (selectedProductId === 'all' || !selectedProductId) return null;
    return safeProducts.find(p => p.id === selectedProductId) || null;
  }, [selectedProductId, safeProducts]);

  /* ------------------------------------------------------------------ */
  /* レンダリング（変更なし）                                            */
  /* ------------------------------------------------------------------ */
  if (loading && clients.length === 0) {
    return (
      <>
        <Header />
        <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
          <CircularProgress color="primary" />
        </Box>
      </>
    );
  }

  return (
    <>
      <Header />
      <Container maxWidth="md" sx={{ px:{xs:2,sm:3}, pb:10 }}>
        {/* 検索バー */}
        <Box sx={{ position:'sticky', top:0, pt:2, pb:1, bgcolor:'background.default', zIndex:10 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="顧客名で検索"
            value={searchTerm}
            onChange={(e)=>setSearchTerm(e.target.value)}
            InputProps={{ startAdornment:(<InputAdornment position="start"><SearchIcon color="action"/></InputAdornment>) }}
            sx={{ mb:2, bgcolor:'white', '& .MuiOutlinedInput-root':{ borderRadius:2, '&.Mui-focused fieldset':{ borderColor:'primary.main', borderWidth:2 } } }}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb:2 }}><AlertTitle>エラー</AlertTitle>{error}</Alert>
        )}

        {/* 製品データエラーの表示 */}
        {safeProducts.length === 0 && products.length > 0 && (
          <Alert severity="warning" sx={{ mb:2 }}>
            <AlertTitle>製品データエラー</AlertTitle>
            利用可能な製品がありません。製品データに問題がある可能性があります。
          </Alert>
        )}

        {/* グループセクション */}
        <GroupSection groups={groups} navigate={navigate} />

        {/* アクティブフィルターチップ */}
        {activeFilterChips.length > 0 && (
          <Box sx={{ p:2, mb:2, bgcolor:'grey.50', borderRadius:2, boxShadow:1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb:1 }}>適用中のフィルター:</Typography>
            <Box sx={{ display:'flex', flexWrap:'wrap', gap:1 }}>
              {activeFilterChips.map(chip => (
                <Chip key={chip.key} label={chip.label} size="small" onDelete={chip.onDelete} color="primary" variant="outlined"/>
              ))}
              <Button size="small" onClick={clearState} startIcon={<ClearIcon/>} sx={{ ml:1 }}>すべてクリア</Button>
            </Box>
          </Box>
        )}

        {/* 顧客一覧 */}
        <ClientSection
          clients={clients}
          products={safeProducts}
          totalElements={totalElements}
          loading={loading}
          searchQuery={searchQuery}
          activeFilters={activeFilters}
          page={page}
          totalPages={totalPages}
          selectedProductId={selectedProductId}
          activeSorting={activeSorting}
          handlePageChange={(e, newPage) => handlePageChange(newPage - 1)}
          handleProductSelect={handleProductSelect}
          getSortingText={getSortingText}
          setFilterOpen={setFilterOpen}
          setSortOpen={setSortOpen}
          navigate={navigate}
        />
      </Container>
      
      {/* 🚀 FilterModal: includeUnregisteredの渡し方を明確化 */}
      <FilterModal
        open={isFilterOpen}
        onClose={()=>setFilterOpen(false)}
        onApply={handleFilterApply}
        onReset={clearState}
        groups={groups}
        selectedGroupIds={selectedGroupIds}
        setSelectedGroupIds={setSelectedGroupIds}
        selectedProduct={selectedProductForModal}
        allFilters={allFilters}
        includeUnregistered={allFilters.includeUnregistered}
      />

      <SortDropdown
        open={isSortOpen}
        onClose={()=>setSortOpen(false)}
        onSortApply={handleSortApply}
        selectedProductId={selectedProductId}
      />
    </>
  );
}